import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { CustomTable } from '../../common/CustomTable';
import DownloadIcon from '@mui/icons-material/Download';

interface VoicemailsOverviewTabProps {
  metrics: any;
  comparison?: any;
  formatWithComparison: (value: string | number, prevValue?: string | number) => React.ReactNode;
  formatDuration: (seconds: number) => string;
  renderMetricCard: (title: string, value: string | number, comparisonValue?: number, rawValue?: number, inverted?: boolean, formatter?: (val: number) => string) => React.ReactNode;
  currentStartDate: string;
  currentEndDate: string;
  previousStartDate: string | null;
  previousEndDate: string | null;
}

export const VoicemailsOverviewTab = ({
  metrics,
  comparison,
  formatWithComparison,
  formatDuration,
  renderMetricCard,
  currentStartDate,
  currentEndDate,
  previousStartDate,
  previousEndDate,
}: VoicemailsOverviewTabProps) => {
  const handleExportKeyMetrics = async () => {
    const { metricsToCSV, downloadZipFile, formatDateForFilename } = await import('../../../utils/exportUtils');

    const keyMetrics = [
      { label: 'Total Voicemails', value: metrics.totalVoicemails, previousValue: comparison?.totalVoicemails },
      { label: 'Inbound', value: metrics.inboundVoicemails, previousValue: comparison?.inboundVoicemails },
      { label: 'Outbound', value: metrics.outboundVoicemails, previousValue: comparison?.outboundVoicemails },
      { label: 'Avg Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
    ];

    const dateStr = formatDateForFilename();
    const csvContent = metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate ?? undefined, previousEndDate ?? undefined);

    await downloadZipFile(
      [{ filename: `${dateStr}-voicemails-key-metrics.csv`, content: csvContent }],
      `${dateStr}-voicemails-key-metrics.zip`,
    );
  };

  return (
    <>
      {/* Key Metrics Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
        <Typography variant='h6'>🎯 Key Metrics</Typography>
        <Tooltip title='Export key metrics'>
          <IconButton size='small' onClick={handleExportKeyMetrics} sx={{ color: '#1a667a' }}>
            <DownloadIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Total Voicemails',
            metrics.totalVoicemails,
            comparison?.totalVoicemails,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Inbound',
            metrics.inboundVoicemails,
            comparison?.inboundVoicemails,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Outbound',
            metrics.outboundVoicemails,
            comparison?.outboundVoicemails,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Avg Duration',
            formatDuration(metrics.averageDuration),
            comparison?.averageDuration,
            metrics.averageDuration,
            true, // Inverted: shorter duration is more efficient
          )}
        </Grid>
      </Grid>

      {/* Tables Section */}
      <Grid container spacing={3}>
        {/* By Status Table */}
        {metrics.voicemailsByStatus && metrics.voicemailsByStatus.length > 0 && (
          <Grid item xs={12} md={6}>
            <CustomTable
              title='By Status'
              columns={[
                { key: 'status', label: 'Status' },
                { key: 'count', label: 'Count' },
              ]}
              rows={metrics.voicemailsByStatus.map((s: any) => {
                const prevData = comparison?.voicemailsByStatus?.find(
                  (prev: any) => prev.status === s.status,
                );
                return {
                  status: s.status,
                  count: formatWithComparison(s.count, prevData?.count),
                };
              })}
              defaultText='No data available'
              loading={false}
              exportable={true}
              exportFilename='voicemails-by-status.csv'
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
